import { useNavigate } from 'react-router-dom';

import { RecordPageShell } from '~/components/layout';
import AssistantIdentityFields from '~/components/pages/ai/digital-assistants/AssistantIdentityFields';
import { Alert, Card, CardBody, CardFooter, CardHeader, FormActions } from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlParams } from '~/hooks/useUrlParams';
import { apiErrorMessage, isNotFound } from '~/lib/apiError';
import { assistantCreateSchema } from '~/lib/ai/digitalAssistant';
import {
  useCreateDigitalAssistantMutation,
  useDuplicateDigitalAssistantMutation,
  useGetDigitalAssistantQuery,
} from '~/modules/query/ai/digitalAssistants';
import type { DigitalAssistant } from '~/modules/query/ai/types';
import { routes } from '~/app/routes';

const FORM_ID = 'digital-assistant-create-form';

interface CreateDraft {
  name: string;
  assistantKey: string;
}

/**
 * `/ai/digital-assistants/new`: a name and a key. With `?duplicate=<id>` it prefills "<name> (copy)"
 * and the new assistant keeps the original's configuration; the key is cleared because the service
 * requires a new one.
 */
export default function DigitalAssistantCreatePage() {
  const navigate = useNavigate();
  const url = useUrlParams();
  const duplicateId = url.get('duplicate');
  const source = useGetDigitalAssistantQuery(duplicateId ?? '', { skip: !duplicateId });
  const [createAssistant] = useCreateDigitalAssistantMutation();
  const [duplicateAssistant] = useDuplicateDigitalAssistantMutation();

  const { draft, patch, dirty } = useRecordDraft<DigitalAssistant | null, CreateDraft>(
    duplicateId ? source.currentData?.data : null,
    {
      id: duplicateId ?? 'new',
      toDraft: (original) => ({
        name: original ? `${original.name} (copy)` : '',
        assistantKey: '',
      }),
    },
  );
  const guard = useUnsavedGuard(dirty);
  const check = useFormValidation(assistantCreateSchema, draft, (field) =>
    field === 'name' ? 'digital-assistant-name' : 'digital-assistant-key',
  );

  const duplicate = Boolean(duplicateId);
  const save = useRecordSave<CreateDraft>({
    save: async ({ name, assistantKey }) => {
      const values = { name: name.trim(), assistantKey: assistantKey.trim() };
      const created = duplicateId
        ? await duplicateAssistant({ id: duplicateId, ...values }).unwrap()
        : await createAssistant(values).unwrap();
      guard.release();
      navigate(routes.aiDigitalAssistant(created.data.id), { replace: true });
    },
    success: duplicate ? 'Digital assistant duplicated' : 'Digital assistant created',
    failure: duplicate
      ? 'The assistant could not be duplicated. Check the key and try again.'
      : 'The assistant could not be created. Check the key and try again.',
  });

  const title = duplicate ? 'Duplicate digital assistant' : 'Create digital assistant';
  const missing = duplicate && source.isError;

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'AI' },
        { label: 'Digital assistants', href: routes.aiDigitalAssistants },
        { label: title },
      ]}
      loading={!draft && !missing}
      notFound={missing && isNotFound(source.error)}
      notFoundTitle="Digital assistant not found"
      backTo={routes.aiDigitalAssistants}
      backLabel="Back to digital assistants"
      error={
        missing && !isNotFound(source.error)
          ? { what: 'the digital assistant', message: apiErrorMessage(source.error) }
          : null
      }
      onRetry={() => source.refetch()}
      guard={guard}
      testId="ai-digital-assistant-new"
    >
      {draft && (
        <form
          id={FORM_ID}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (check.submit()) save.submit(draft);
          }}
        >
          <Card>
            <CardHeader
              title="Details"
              description={
                duplicate
                  ? "The duplicate keeps the original's configuration and takes a new key."
                  : 'Name it and give it the key the API routes requests by. Every other setting is edited on its own page.'
              }
            />
            <CardBody className="space-y-6">
              {save.error && <Alert variant="error" message={save.error} />}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <AssistantIdentityFields
                  isNew
                  name={draft.name}
                  assistantKey={draft.assistantKey}
                  onNameChange={(name) => patch({ name })}
                  onNameBlur={() => check.touch('name')}
                  nameError={check.errors.name}
                  onKeyChange={(assistantKey) => patch({ assistantKey })}
                  onKeyBlur={() => check.touch('assistantKey')}
                  keyError={check.errors.assistantKey}
                />
              </div>
            </CardBody>
            <CardFooter>
              <FormActions
                form={FORM_ID}
                discardHref={routes.aiDigitalAssistants}
                discardLabel="Cancel"
                saveLabel={duplicate ? 'Duplicate assistant' : 'Create assistant'}
                busyLabel={duplicate ? 'Duplicating…' : 'Creating…'}
                busy={save.busy}
                saveTestId="ai-digital-assistants-create-submit"
                discardTestId="ai-digital-assistants-create-cancel"
              />
            </CardFooter>
          </Card>
        </form>
      )}
    </RecordPageShell>
  );
}
